import React, { useState } from 'react';
import { standaloneApi } from './api/client';
import type { StandaloneUser } from './api/client';

const C = { navy: '#002767', red: '#BD0C31', g50: '#f9fafb', g100: '#f3f4f6', g200: '#e5e7eb', g300: '#d1d5db', g500: '#6b7280', g700: '#374151', g900: '#111827' };

type Screen = 'login' | 'register' | 'forgot' | 'reset';

interface Props {
    onAuth: (user: StandaloneUser) => void;
}

export default function AuthScreen({ onAuth }: Props) {
    const [screen, setScreen] = useState<Screen>('login');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Login
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPass, setLoginPass] = useState('');

    // Register
    const [regFirstName, setRegFirstName] = useState('');
    const [regLastName, setRegLastName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPass, setRegPass] = useState('');
    const [regConfirm, setRegConfirm] = useState('');
    const [regCompany, setRegCompany] = useState('');

    // Forgot / Reset
    const [forgotEmail, setForgotEmail] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [resetPass, setResetPass] = useState('');
    const [resetConfirm, setResetConfirm] = useState('');

    const clearAll = () => { setError(''); setSuccess(''); };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        clearAll();
        if (!loginEmail || !loginPass) { setError('Please fill in all fields'); return; }
        setLoading(true);
        try {
            const { token, user } = await standaloneApi.login({ email: loginEmail, password: loginPass });
            localStorage.setItem('rei_token', token);
            onAuth(user);
        } catch (err) { setError((err as Error).message); }
        setLoading(false);
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        clearAll();
        if (!regFirstName || !regLastName || !regEmail || !regPass) { setError('Please fill in all required fields'); return; }
        if (regPass.length < 6) { setError('Password must be at least 6 characters'); return; }
        if (regPass !== regConfirm) { setError('Passwords do not match'); return; }
        setLoading(true);
        try {
            const { token, user } = await standaloneApi.register({
                email: regEmail,
                password: regPass,
                firstName: regFirstName,
                lastName: regLastName,
                company: regCompany,
            });
            localStorage.setItem('rei_token', token);
            onAuth(user);
        } catch (err) { setError((err as Error).message); }
        setLoading(false);
    };

    const handleForgot = async (e: React.FormEvent) => {
        e.preventDefault();
        clearAll();
        if (!forgotEmail) { setError('Please enter your email'); return; }
        setLoading(true);
        try {
            const res = await standaloneApi.forgotPassword(forgotEmail);
            setSuccess(res.message);
            if (res.resetToken) {
                setResetToken(res.resetToken);
                setScreen('reset');
            }
        } catch (err) { setError((err as Error).message); }
        setLoading(false);
    };

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        clearAll();
        if (!resetToken || !resetPass) { setError('Please fill in all fields'); return; }
        if (resetPass.length < 6) { setError('Password must be at least 6 characters'); return; }
        if (resetPass !== resetConfirm) { setError('Passwords do not match'); return; }
        setLoading(true);
        try {
            const { token } = await standaloneApi.resetPassword(resetToken, resetPass);
            localStorage.setItem('rei_token', token);
            const { user } = await standaloneApi.getMe();
            onAuth(user);
        } catch (err) { setError((err as Error).message); }
        setLoading(false);
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '10px 14px', border: `1px solid ${C.g200}`, borderRadius: 8,
        fontSize: 14, outline: 'none', transition: 'border-color 0.15s',
    };
    const btnStyle: React.CSSProperties = {
        width: '100%', padding: '11px', background: C.navy, color: '#fff', border: 'none', borderRadius: 8,
        fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.6 : 1,
    };
    const linkStyle: React.CSSProperties = { color: C.navy, cursor: 'pointer', fontWeight: 500, fontSize: 13 };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f0f4ff 0%, #e8edf5 100%)', padding: 20 }}>
            <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,39,103,0.10)', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ background: `linear-gradient(135deg, #001a4d 0%, ${C.navy} 60%, #003a99 100%)`, padding: '32px 32px 24px', color: '#fff', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.red }} />
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', opacity: 0.85 }}>REI Software</span>
                    </div>
                    <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Offer Analysis</h1>
                    <p style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                        {screen === 'login' ? 'Sign in to your account' :
                         screen === 'register' ? 'Create your account' :
                         screen === 'forgot' ? 'Reset your password' : 'Set new password'}
                    </p>
                </div>

                {/* Body */}
                <div style={{ padding: '28px 32px 32px' }}>
                    {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}
                    {success && <div style={{ background: '#f0fdf4', color: '#15803d', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{success}</div>}

                    {/* LOGIN */}
                    {screen === 'login' && (
                        <form onSubmit={handleLogin}>
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Email</label>
                                <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Password</label>
                                <input type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} style={inputStyle} placeholder="Enter password" />
                            </div>
                            <button type="submit" disabled={loading} style={btnStyle}>{loading ? 'Signing in...' : 'Sign In'}</button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                                <span style={linkStyle} onClick={() => { clearAll(); setScreen('forgot'); }}>Forgot password?</span>
                                <span style={linkStyle} onClick={() => { clearAll(); setScreen('register'); }}>Create account</span>
                            </div>
                        </form>
                    )}

                    {/* REGISTER */}
                    {screen === 'register' && (
                        <form onSubmit={handleRegister}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>First Name *</label>
                                    <input value={regFirstName} onChange={e => setRegFirstName(e.target.value)} style={inputStyle} placeholder="John" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Last Name *</label>
                                    <input value={regLastName} onChange={e => setRegLastName(e.target.value)} style={inputStyle} placeholder="Doe" />
                                </div>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Email *</label>
                                <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" />
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Company</label>
                                <input value={regCompany} onChange={e => setRegCompany(e.target.value)} style={inputStyle} placeholder="Optional" />
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Password *</label>
                                <input type="password" value={regPass} onChange={e => setRegPass(e.target.value)} style={inputStyle} placeholder="At least 6 characters" />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Confirm Password *</label>
                                <input type="password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} style={inputStyle} placeholder="Re-enter password" />
                            </div>
                            <button type="submit" disabled={loading} style={btnStyle}>{loading ? 'Creating account...' : 'Create Demo Account'}</button>
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <span style={linkStyle} onClick={() => { clearAll(); setScreen('login'); }}>Already have an account? Sign in</span>
                            </div>
                        </form>
                    )}

                    {/* FORGOT PASSWORD */}
                    {screen === 'forgot' && (
                        <form onSubmit={handleForgot}>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Email</label>
                                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" />
                            </div>
                            <button type="submit" disabled={loading} style={btnStyle}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <span style={linkStyle} onClick={() => { clearAll(); setScreen('login'); }}>Back to sign in</span>
                            </div>
                        </form>
                    )}

                    {/* RESET PASSWORD */}
                    {screen === 'reset' && (
                        <form onSubmit={handleReset}>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Reset Token</label>
                                <input value={resetToken} onChange={e => setResetToken(e.target.value)} style={inputStyle} placeholder="Paste reset token" />
                            </div>
                            <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>New Password</label>
                                <input type="password" value={resetPass} onChange={e => setResetPass(e.target.value)} style={inputStyle} placeholder="At least 6 characters" />
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, color: C.g700, display: 'block', marginBottom: 4 }}>Confirm Password</label>
                                <input type="password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)} style={inputStyle} placeholder="Re-enter password" />
                            </div>
                            <button type="submit" disabled={loading} style={btnStyle}>{loading ? 'Resetting...' : 'Reset Password'}</button>
                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <span style={linkStyle} onClick={() => { clearAll(); setScreen('login'); }}>Back to sign in</span>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
